import { useEffect } from "react";

/**
 * Lightweight SEO/Open-Graph manager.
 * Writes <title> and a fixed set of <meta> tags into <head>, then restores
 * them on unmount so each route paints its own metadata.
 *
 * Usage:
 *   useSeo({
 *     title: "Doñana Horseback at Dawn — Viaja de Verdad",
 *     description: "...",
 *     image: "/static/experiences/donana-horseback/hero.png",
 *     url: "https://.../experiences/donana-horseback",
 *     type: "article",
 *     locale: "en_US",
 *   });
 */

const DEFAULTS = {
  siteName: "Viaja de Verdad",
  title: "Viaja de Verdad — Singular Experiences",
  description: "Singular experiences across Spain, Italy and Portugal — curated by Viaja de Verdad.",
  image: "/static/og/og-default.jpg",
  type: "website",
  locale: "en_US",
};

function absUrl(maybeRelative) {
  if (!maybeRelative) return "";
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  if (typeof window === "undefined") return maybeRelative;
  try {
    return new URL(maybeRelative, window.location.origin).toString();
  } catch {
    return maybeRelative;
  }
}

function setMeta(selector, attr, value) {
  if (typeof document === "undefined") return null;
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    // Reconstitute the right attribute (name vs property)
    const m = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return { el, prev, created };
}

export function useSeo(seo = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const merged = { ...DEFAULTS, ...seo };
    const url = absUrl(merged.url || (typeof window !== "undefined" ? window.location.href : ""));
    const image = absUrl(merged.image);

    // Title
    const prevTitle = document.title;
    document.title = merged.title;

    // Meta tags we manage
    const ops = [
      ["meta[name=\"description\"]", "content", merged.description],
      ["meta[property=\"og:site_name\"]", "content", merged.siteName],
      ["meta[property=\"og:type\"]", "content", merged.type],
      ["meta[property=\"og:title\"]", "content", merged.title],
      ["meta[property=\"og:description\"]", "content", merged.description],
      ["meta[property=\"og:image\"]", "content", image],
      ["meta[property=\"og:url\"]", "content", url],
      ["meta[property=\"og:locale\"]", "content", merged.locale],
      ["meta[name=\"twitter:card\"]", "content", "summary_large_image"],
      ["meta[name=\"twitter:title\"]", "content", merged.title],
      ["meta[name=\"twitter:description\"]", "content", merged.description],
      ["meta[name=\"twitter:image\"]", "content", image],
    ];

    const applied = ops.map(([sel, attr, val]) => ({ sel, attr, ...setMeta(sel, attr, val) }));

    return () => {
      document.title = prevTitle;
      applied.forEach(({ el, attr, prev, created }) => {
        if (!el) return;
        if (created) el.remove();
        else if (prev !== null && prev !== undefined) el.setAttribute(attr, prev);
      });
    };
  }, [
    seo.title,
    seo.description,
    seo.image,
    seo.url,
    seo.type,
    seo.locale,
    seo.siteName,
  ]);
}
